import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { TPU_APP_NAV, LOGO_NAV, COMPANY } from "../data/content";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center">
    <img src={LOGO_NAV} alt="PlastoNord GmbH Logo" className="h-9 w-auto object-contain md:h-10" height="40" />
  </Link>
);

export function Navbar() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/produkte", label: t("nav.products") },
    { to: "/anwendungen", label: t("nav.applications") },
    { to: "/branchen", label: t("nav.industries") },
    { to: "/qualitaet", label: t("nav.quality") },
    { to: "/logistik", label: t("nav.logistics") },
    { to: "/wissen", label: t("nav.knowledge") },
    { to: "/ueber-uns", label: t("nav.about") },
  ];

  return (
    <header className={`sticky top-0 z-50 glass-nav transition-shadow ${scrolled ? "shadow-[0_8px_30px_-18px_rgba(22,24,29,.4)]" : ""}`}>
      <nav className="pn-container flex h-16 items-center justify-between md:h-[72px]">
        <Logo />
        <div className="hidden items-center gap-1 lg:flex">
          {/* TPU dropdown */}
          <div className="group relative">
            <NavLink to="/tpu" data-testid="nav-tpu"
              className={({ isActive }) => `flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-[#E2611C]" : "text-[#14171c] hover:text-[#E2611C]"}`}>
              TPU <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
            </NavLink>
            <div className="invisible absolute left-0 top-full w-64 translate-y-2 rounded-xl border border-[#e4e9f0] bg-white p-2 opacity-0 shadow-xl transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100" data-testid="tpu-dropdown">
              <Link to="/tpu" className="block rounded-lg px-3 py-2 text-sm font-medium text-[#16181D] hover:bg-[#FBEADD]">{lang === "de" ? "TPU Übersicht" : "TPU overview"}</Link>
              {TPU_APP_NAV.map(([path, label]) => (
                <Link key={path} to={path} className="block rounded-lg px-3 py-2 text-sm text-[#4a4d52] hover:bg-[#FBEADD] hover:text-[#16181D]">{label[lang]}</Link>
              ))}
            </div>
          </div>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-[#E2611C]" : "text-[#14171c] hover:text-[#E2611C]"}`}>
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <a href={COMPANY.portalUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-portal"
            className="hidden items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-[#14171c] transition-colors hover:text-[#E2611C] lg:flex">
            {t("nav.portal")} <ArrowUpRight size={14} />
          </a>
          <button onClick={toggle} data-testid="lang-toggle"
            className="hidden items-center gap-1.5 rounded-full border border-[#e4e9f0] px-3 py-1.5 text-sm font-medium text-[#14171c] transition-colors hover:border-[#E2611C] sm:flex">
            <Globe size={15} /> {lang.toUpperCase()}
          </button>
          <Link to="/kontakt" data-testid="nav-contact-cta" className="btn-primary !px-5 !py-2 text-sm">
            {t("cta.contact")}
          </Link>
          <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="border-t border-[#e4e9f0] bg-white lg:hidden" data-testid="mobile-menu">
          <div className="pn-container flex flex-col py-4">
            <NavLink to="/tpu" className="border-b border-[#f0f2f5] py-3 font-semibold text-[#E2611C]">TPU</NavLink>
            {links.concat([{ to: "/andere-kunststoffe", label: lang === "de" ? "Andere Kunststoffe" : "Other plastics" }, { to: "/downloads", label: t("nav.downloads") }]).map((l) => (
              <NavLink key={l.to} to={l.to} className="border-b border-[#f0f2f5] py-3 text-[#14171c]">{l.label}</NavLink>
            ))}
            <a href={COMPANY.portalUrl} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-portal" className="flex items-center gap-1 border-b border-[#f0f2f5] py-3 font-medium text-[#E2611C]">{t("nav.portal")} <ArrowUpRight size={14} /></a>
            <button onClick={toggle} className="mt-3 flex items-center gap-2 text-[#E2611C]"><Globe size={16} /> {lang === "de" ? "Switch to English" : "Auf Deutsch"}</button>
          </div>
        </div>
      )}
    </header>
  );
}
